import { Injectable, computed, inject, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MixedSchema, versionedSchemaToHtml } from '@principle-theorem/editor';
import { Referral } from '@principle-theorem/principle-core';
import {
  IBrand,
  IReferral,
  ReferralActor,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  WithRef,
  httpsCallable,
  getError,
  serialise,
  snapshot,
} from '@principle-theorem/shared';
import { EditorPresetsService } from '@principle-theorem/ng-interactions';
import { ReferralInteractionService } from '../services/referral-interaction.service';

@Injectable()
export class ReferralEmailService {
  private _snackBar = inject(MatSnackBar);
  private _interactionService = inject(ReferralInteractionService);
  private _editorPresets = inject(EditorPresetsService);
  private _isSending = signal(false);
  public isSending = computed(() => this._isSending());

  async sendReferralEmail(
    brandRef: DocumentReference<IBrand>,
    referral: WithRef<IReferral>,
    currentUser: ReferralActor,
    subject: string,
    content: MixedSchema
  ): Promise<void> {
    this._isSending.set(true);

    try {
      const contact = Referral.getContact(referral);
      const staffer = Referral.getStaffer(referral);

      const hasRequiredData =
        staffer &&
        contact &&
        !!referral.externalUserEmail &&
        referral.patientDetails &&
        referral.practice;

      if (!hasRequiredData) {
        this._snackBar.open('Missing required data for email');
        return;
      }

      const extensions = this._editorPresets.defaultToHTMLExtensions();
      const contentHtml = versionedSchemaToHtml(content, extensions);

      await snapshot(
        httpsCallable('http-referral-sendReferralContactEmail')(
          serialise({
            brandRef,
            referralRef: referral.ref,
            subject,
            contentHtml,
            actor: currentUser,
          })
        )
      );

      await this._interactionService.createEmailSentToContactInteraction(
        referral,
        staffer,
        currentUser,
        subject,
        content
      );

      this._snackBar.open('Email sent to recipient');
    } catch (error) {
      this._snackBar.open('Failed to send email');
      throw new Error(getError(error));
    } finally {
      this._isSending.set(false);
    }
  }
}
